/**
 * Add people to a Team, from the header's "On this Team" list.
 *
 * Everyone in the workspace, searchable, as a checklist; whoever is already on
 * the Team is listed but cannot be picked. Adding goes through the Team's
 * member route one person at a time.
 *
 * A personal Team's first colleague shares it, which changes what it is: it
 * shows in their sidebar, they can act on its tickets, and only its people
 * and admins see it (the server keeps a shared personal Team to members only).
 * So that add asks first, and offers the name a shared Team should have.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Btn } from "../../queue/index.js";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog.js";
import { toast } from "../../ui/toast.js";
import { captureUiError } from "../../sentry.js";
import {
  TeamWriteError,
  addTeamMember,
  fetchWorkspacePeople,
  renameTeam,
  type TeamMember,
  type WorkspacePerson,
} from "../teamsApi.js";
import { Face } from "./Face.js";
import { ADD_PEOPLE_CAN, andList, personFirstName, personName, sharedTeamName } from "./people.js";
import "./people.css";

export interface AddPeopleDialogProps {
  open: boolean;
  onClose: () => void;
  deskId: string;
  teamName: string;
  members: readonly TeamMember[];
  /** The Team is personal: adding the first colleague shares it. */
  personal: boolean;
  viewerId: string | null;
  /** Called once the people are on the Team (and any rename landed). */
  onAdded: () => void | Promise<void>;
}

/** "Add Kara", "Add 2 people". */
export function addLabel(names: readonly string[]): string {
  if (names.length === 0) return "Add people";
  return names.length === 1 ? `Add ${names[0]}` : `Add ${names.length} people`;
}

/** Add each person in turn; the first refusal stops it and names who. */
async function addEach(
  deskId: string,
  people: readonly WorkspacePerson[],
): Promise<{ added: string[]; failure: string | null }> {
  const added: string[] = [];
  for (const person of people) {
    try {
      await addTeamMember(deskId, person.userId);
      added.push(person.userId);
    } catch (err) {
      if (!(err instanceof TeamWriteError) || err.status >= 500) {
        captureUiError(err, { area: "Teams", operation: "add_people", deskId });
      }
      const why = err instanceof Error ? err.message : "Try again.";
      return { added, failure: `Could not add ${personFirstName(person)}: ${why}` };
    }
  }
  return { added, failure: null };
}

/** The shared Team's new name; the refusal in words when it did not land. */
async function renameAfterShare(deskId: string, name: string): Promise<string | null> {
  try {
    await renameTeam(deskId, name);
    return null;
  } catch (err) {
    captureUiError(err, { area: "Teams", operation: "add_people_rename", deskId });
    const why = err instanceof Error ? err.message : "Try again under Team settings.";
    return `They are on the Team, but it could not be renamed: ${why}`;
  }
}

export function AddPeopleDialog(props: AddPeopleDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={(next) => (next ? undefined : props.onClose())}>
      {props.open && <AddPeopleBody {...props} />}
    </Dialog>
  );
}

function AddPeopleBody({
  onClose,
  deskId,
  teamName,
  members,
  personal,
  viewerId,
  onAdded,
}: AddPeopleDialogProps) {
  const [people, setPeople] = useState<WorkspacePerson[] | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [step, setStep] = useState<"pick" | "share">("pick");
  const owner = members.find((member) => member.role === "owner") ?? null;
  const [name, setName] = useState(() => sharedTeamName(owner));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWorkspacePeople()
      .then((list) => {
        if (!cancelled) setPeople(list);
      })
      .catch((err: unknown) => {
        captureUiError(err, {
          area: "Teams",
          operation: "add_people_read_members",
          deskId,
          severity: "warning",
        });
        if (!cancelled) setReadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [deskId]);

  const onTeam = useMemo(() => new Set(members.map((member) => member.userId)), [members]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (people ?? []).filter(
      (person) =>
        !q || `${person.displayName ?? ""} ${person.email ?? ""}`.toLowerCase().includes(q),
    );
  }, [people, query]);
  // In the order they were picked, so the adds go out in that order.
  const pickedPeople = picked.flatMap(
    (userId) => (people ?? []).find((person) => person.userId === userId) ?? [],
  );
  const firstNames = pickedPeople.map((person) => personFirstName(person));
  // Only the first colleague shares it: a Team with two people is no longer personal.
  const shares = personal && members.length <= 1;
  const viewerOwns = owner !== null && owner.userId === viewerId;

  const toggle = (userId: string) =>
    setPicked((prev) =>
      prev.includes(userId) ? prev.filter((entry) => entry !== userId) : [...prev, userId],
    );

  const add = async () => {
    setBusy(true);
    setError(null);
    const { added, failure: addFailure } = await addEach(deskId, pickedPeople);
    let failure = addFailure;
    let renamed: string | null = null;
    // Renaming is the charter write, which only the Team's owner makes.
    const wanted = name.trim();
    if (!failure && step === "share" && viewerOwns && wanted && wanted !== teamName.trim()) {
      const refusal = await renameAfterShare(deskId, wanted);
      if (refusal) failure = refusal;
      else renamed = wanted;
    }
    // Whoever made it on is on the Team, whatever came after: show them, and
    // stop offering them as still to add.
    setPicked((prev) => prev.filter((userId) => !added.includes(userId)));
    try {
      if (added.length > 0) await onAdded();
    } catch (err) {
      captureUiError(err, {
        area: "Teams",
        operation: "add_people_refresh",
        deskId,
        severity: "warning",
      });
    }
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    const names = pickedPeople
      .filter((person) => added.includes(person.userId))
      .map(personFirstName);
    toast.success(
      `${andList(names)} ${names.length === 1 ? "is" : "are"} on ${renamed ?? teamName}.`,
    );
    onClose();
  };

  if (step === "share") {
    const who = andList(firstNames);
    const theirs = firstNames.length === 1 ? `${firstNames[0]}'s` : "their";
    const whose = viewerOwns ? "your" : `${owner ? personFirstName(owner) : "its owner"}'s`;
    return (
      <DialogContent className="addpeople" data-testid="share-personal-team">
        <DialogTitle>
          Share {whose} personal Team with {who}?
        </DialogTitle>
        <DialogDescription asChild>
          <ul className="bul">
            <li>
              It shows in {theirs} sidebar and{" "}
              {firstNames.length === 1 ? `${firstNames[0]} can` : "they can"} act on its tickets.
              Only its people and admins see it.
            </li>
            <li>It still picks only {whose} tickets. Change that under Whose tickets.</li>
          </ul>
        </DialogDescription>
        {viewerOwns && (
          <label className="namefield">
            <span className="label">Team name</span>
            <input
              value={name}
              maxLength={120}
              aria-label="Team name"
              data-testid="share-team-name"
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        )}
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="verbs">
          <Btn variant="quiet" disabled={busy} onClick={() => setStep("pick")}>
            Back
          </Btn>
          <Btn
            variant="primary"
            disabled={busy}
            onClick={() => void add()}
            data-testid="share-confirm"
          >
            {firstNames.length === 1
              ? `Share with ${firstNames[0]}`
              : `Share with ${firstNames.length} people`}
          </Btn>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="addpeople" data-testid="add-people">
      <DialogTitle>Add people to {teamName || "this Team"}</DialogTitle>
      <DialogDescription className="can">{ADD_PEOPLE_CAN}</DialogDescription>
      <div className="in">
        <input
          type="search"
          autoFocus
          value={query}
          placeholder="Search people in the workspace"
          aria-label="Search people in the workspace"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="plist" data-testid="add-people-list">
        {people === null && !readFailed && (
          <span className="fact">Reading the workspace's people.</span>
        )}
        {readFailed && (
          <span className="fact">
            Could not read the workspace's people. Close this and try again.
          </span>
        )}
        {people !== null && shown.length === 0 && <span className="fact">Nobody matches.</span>}
        {shown.map((person) => {
          const already = onTeam.has(person.userId);
          const label = personName(person);
          return (
            <label key={person.userId} className={`prow${already ? " off" : ""}`}>
              <input
                type="checkbox"
                disabled={already || busy}
                checked={already || picked.includes(person.userId)}
                aria-label={label}
                onChange={() => toggle(person.userId)}
              />
              <Face userId={person.userId} name={label} avatarUrl={person.avatarUrl} size={28} />
              <span className="who">
                <span className="nm">{label}</span>
                <span className="sub">
                  {already ? "Already on this Team" : (person.email ?? "")}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <p className="invite">
        Someone not in the workspace?{" "}
        <Link to="/settings/people" onClick={onClose}>
          Invite them first
        </Link>
        .
      </p>
      {error && (
        <p className="err" role="alert">
          {error}
        </p>
      )}
      <div className="verbs">
        <Btn variant="quiet" disabled={busy} onClick={onClose}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          disabled={busy || picked.length === 0}
          data-testid="add-people-confirm"
          onClick={() => (shares ? setStep("share") : void add())}
        >
          {addLabel(firstNames)}
        </Btn>
      </div>
    </DialogContent>
  );
}
